import { useEffect, useRef } from 'react'
import { useFirebaseProvider } from '@/app/providers/useFirebaseProvider'

type ResolveGoogleAnalyticsUserIdParams = {
  isAuthLoading: boolean
  userId: string | undefined
  hasTrackedUserId: boolean
}

/**
 * GA4 に送る `user_id` を現在の認証状態から決定する。
 * - 未ログイン継続なら送らない
 * - ログイン済みなら Firebase UID を送る
 * - 一度ログインした後のログアウトでは `null` を送って解除する
 */
export const resolveGoogleAnalyticsUserId = ({
  isAuthLoading,
  userId,
  hasTrackedUserId,
}: ResolveGoogleAnalyticsUserIdParams): string | null | undefined => {
  if (isAuthLoading) return undefined
  if (userId) return userId
  if (hasTrackedUserId) return null
  return undefined
}

const applyGoogleAnalyticsUserId = (userId: string | null): boolean => {
  if (!window.gtag) return false

  window.gtag('set', { user_id: userId })
  return true
}

export const useGoogleAnalyticsUserId = () => {
  const { user, isLoading: isAuthLoading } = useFirebaseProvider()
  const hasTrackedUserIdRef = useRef(false)

  useEffect(() => {
    const nextUserId = resolveGoogleAnalyticsUserId({
      isAuthLoading,
      userId: user?.uid,
      hasTrackedUserId: hasTrackedUserIdRef.current,
    })

    if (nextUserId === undefined) return
    if (!applyGoogleAnalyticsUserId(nextUserId)) return

    hasTrackedUserIdRef.current = nextUserId !== null
  }, [isAuthLoading, user?.uid])
}
